import { ArrowBack } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import { Tooltip } from "@mui/material";
import { Col, Row, Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, FieldArray } from "formik";
import { Button } from "../../common/Button";
import Editor from "@monaco-editor/react";
import { pd } from "pretty-data";
import * as Yup from "yup";
import * as monaco from "monaco-editor";
import { useEffect, useRef, useState } from "react";
import { createIDP, describeIDP, editIDP } from "../../api/IDP";
import { useSelector } from "react-redux";
import { failed, success } from "../../common/Toastify";
import { FullPageSpinner } from "../../common/Spinner/FullPageSpinner";
import { COGNITO_ATTRIBUTES } from "./constants";
import { useMemo } from "react";
import { validation, conditionalValidation } from "../../../utils/constants";

const IDPUpdate = () => {
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const providerName = params.get("providerName") || "";
  const idpOrgId = providerName ? providerName.split("-").pop() : undefined;
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const orgId = useSelector(
    (state) => state?.auth?.user?.organizations?.[0]?.id
  );
  const [isLoading, setIsLoading] = useState(false);
  const [IDPData, setIDPData] = useState({});
  const [selectedOrgId, setSelectedOrgId] = useState(orgId);
  const userRoles = useSelector(
    (state) => state?.auth?.user?.["cognito:groups"]
  );
  const isSuperAdmin = userRoles?.includes("Super-Admin");
  const [organizations, setOrganizations] = useState([]);
  const MANDATORY_COGNITO_ATTRS = ["email", "custom:saml_unique_id", "custom:role"];
  const latestReqRef = useRef(0);
  const roleMapReady = IDPData?.roleGroupMapping !== undefined;

  const handleFileUpload = (e, setFieldValue) => {
    const file = e.target.files[0];
    if (file && file.type === "text/xml") {
      const reader = new FileReader();
      reader.onload = (event) => {
        const rawXml = event.target.result;

        // Basic well-formed XML validation
        try {
          const parser = new DOMParser();
          const parsed = parser.parseFromString(rawXml, "application/xml");
          const parseError = parsed.getElementsByTagName("parsererror");
          if (parseError.length > 0) {
            throw new Error("Invalid XML file");
          }

          const prettyXml = pd.xml(rawXml);
          setFieldValue("xmlMetadata", prettyXml);
        } catch (err) {
          alert("The uploaded file is not a valid XML.");
        }
      };
      reader.readAsText(file);
    } else {
      alert("Please upload a valid XML file.");
    }
  };
  const handleOrgChange = (event) => {
    const value = event?.target?.value;
    setSelectedOrgId(value);
  };

  // Validation schema for formik
  const validationSchema = Yup.object().shape({
    providerName: conditionalValidation(
      Yup.string()
      .max(64, "Maximum 64 characters allowed")
      .test(
        "no-underscore",
        "Provider Name cannot contain underscore '_'",
        (value) => !value || !value.includes("_")
      )
      .matches(/^[A-Za-z0-9-]+$/, "Only letters, numbers, and '-' are allowed"),
      {
        validation,
        mandatory: true,
        label: "Provider Name"
      }),
    idpIdentifier: 
      conditionalValidation(
        Yup.string()
          .max(64, "Maximum 64 characters allowed"),
          {
            validation,
            mandatory: true,
            label: "Identifier Domain"
          }),
    
    xmlMetadata: Yup.string().required("SAML metadata is required"), // this is staying as is, because some validation would not work in the base validation.
    
    attributeMappings: conditionalValidation(
      Yup.array()
        .of(
          Yup.object().shape({
            cognitoAttribute: Yup.string().required("Select a Cognito attribute"),
            samlAttribute: Yup.string().required("Enter the SAML attribute name"),
          })
        )
        .min(1, "At least one attribute mapping is required")
        .test(
          "has-custom-role",
          "Mapping for custom:role is required",
          (list) => Array.isArray(list)
            ? list.some((m) => m.cognitoAttribute === "custom:role" && !!m.samlAttribute)
            : false
        ),
        { validation }
      ),
    orgAdminGroup: conditionalValidation(
      Yup.string().trim().required("Organization Admin Group is required"), 
      {
        validation, 
        mandatory: true, 
        label: "Organization Admin Group"}),
    navigatorGroup: conditionalValidation(
      Yup.string().trim().required("Navigator Group is required"),
      {validation, label: "Navigator Group"}),
    clinicianGroup: conditionalValidation(
      Yup.string().trim().required("Clinician Group is required"),
      {validation, label: "Clinical Group"})
  });

  const effectiveOrgId = useMemo(() => {
    return (isSuperAdmin && idpOrgId) ? idpOrgId : orgId;
  }, [isSuperAdmin, idpOrgId, orgId]);

  useEffect(() => {
    if (effectiveOrgId && selectedOrgId !== effectiveOrgId) {
      setSelectedOrgId(effectiveOrgId);
    }
  }, [effectiveOrgId]);


  useEffect(() => {
    if (!providerName) return;
    if (isSuperAdmin && idpOrgId) {
      setSelectedOrgId(idpOrgId);
    } else if (orgId) {
      setSelectedOrgId(orgId);
    }
  }, [isSuperAdmin, idpOrgId, orgId, providerName]);

  useEffect(() => {
    if (!providerName || !effectiveOrgId) return;
    fetchIDPData(effectiveOrgId);
  }, [providerName, effectiveOrgId]);

  const fetchIDPData = async (orgIdParam) => {
    const reqId = ++latestReqRef.current;
    try {
      const res = await describeIDP(providerName, orgIdParam);
      if (reqId !== latestReqRef.current) return;
      if (res?.status === true) {
        const idp = res?.identityProviders?.IdentityProvider || {};
        const roleGroupMapping = res?.roleGroupMapping || {};
        setIDPData({ ...idp, roleGroupMapping });
      } else {
        setIDPData({});
      }
    } catch (e) {
      setIDPData({});
      failed("Failed to load IDP data")
    }
  };

  const buildInitialMappings = () => {
    const base = IDPData?.AttributeMapping
      ? Object.entries(IDPData.AttributeMapping).map(([cognitoAttr, samlAttr]) => ({
        cognitoAttribute: cognitoAttr,
        samlAttribute: samlAttr,
      }))
      : [{ cognitoAttribute: "", samlAttribute: "" }];

    MANDATORY_COGNITO_ATTRS.forEach((attr) => {
      if (!base.some((m) => m.cognitoAttribute === attr)) {
        base.push({ cognitoAttribute: attr, samlAttribute: "" });
      }
    });
    return base;
  };

  const safePrettyXml = (xml) => {
    if (!xml || typeof xml !== "string" || xml.trim() === "") return "";
    try { return pd.xml(xml); } catch { return xml; }
  };
  const toBoolean = (val) => val === true || val === "true";

  const initialValues = useMemo(() => ({
    providerName: IDPData?.ProviderName || "",
    xmlMetadata: safePrettyXml(IDPData?.ProviderDetails?.MetadataFile || ""),
    attributeMappings: buildInitialMappings(),
    idpIdentifier: IDPData?.IdpIdentifiers?.[0] || "",
    orgAdminGroup: IDPData?.roleGroupMapping?.["Organization Administrator"] || "",
    navigatorGroup: IDPData?.roleGroupMapping?.["Navigator"] || "",
    clinicianGroup: IDPData?.roleGroupMapping?.["Clinician"] || "",
    enableIDPSignout: toBoolean(IDPData?.ProviderDetails?.IDPSignout),
  }), [IDPData, effectiveOrgId]);


  return (
    <>
      {isLoading && <FullPageSpinner />}

      <section className="common-listing">
        <div className="heading-wrap mb-3">
          <Tooltip title="Go back">
            <div
              style={{ marginRight: "1rem", cursor: "pointer" }}
              onClick={() => navigate(-1)}
            >
              <ArrowBack />
            </div>
          </Tooltip>
        </div>

        {IDPData?.ProviderName && roleMapReady ? (
          <div className="custom-card p-4">
            <Formik
              key={`${effectiveOrgId}-${IDPData?.ProviderName}-${JSON.stringify(IDPData?.roleGroupMapping || {})}`}
              enableReinitialize={true}
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, actions) => {
                values.providerName = values.providerName.trim();
                values.idpIdentifier = values.idpIdentifier.trim();
                values.orgAdminGroup = values.orgAdminGroup.trim();
                values.navigatorGroup = values.navigatorGroup.trim();
                values.clinicianGroup = values.clinicianGroup.trim();
                const parser = new DOMParser();
                const parsed = parser.parseFromString(
                  values.xmlMetadata,
                  "application/xml"
                );
                const parseError = parsed.getElementsByTagName("parsererror");

                if (parseError.length > 0) {
                  // Full innerText from the <parsererror> tag
                  const fullError = parseError[0].textContent || "Invalid XML";

                  // Extract only the first line of the error
                  const cleanedError = fullError.split("\n")[0].trim();

                  actions.setFieldError("xmlMetadata", cleanedError);

                  // Extract line/column number for Monaco marker
                  const match = cleanedError.match(
                    /line (\d+) at column (\d+)/i
                  );
                  const lineNumber = match ? parseInt(match[1]) : 1;
                  const column = match ? parseInt(match[2]) : 1;

                  if (editorRef.current) {
                    monaco.editor.setModelMarkers(
                      editorRef.current.getModel(),
                      "owner",
                      [
                        {
                          startLineNumber: lineNumber,
                          startColumn: column,
                          endLineNumber: lineNumber,
                          endColumn: column + 1,
                          message: cleanedError,
                          severity: monaco.MarkerSeverity.Error,
                        },
                      ]
                    );
                  }

                  return;
                }

                // Clear markers if valid
                if (editorRef.current) {
                  monaco.editor.setModelMarkers(
                    editorRef.current.getModel(),
                    "owner",
                    []
                  );
                }

                const attributeMappingObject = values.attributeMappings.reduce(
                  (acc, item) => {
                    if (item.cognitoAttribute && item.samlAttribute) {
                      acc[item.cognitoAttribute] = item.samlAttribute;
                    }
                    return acc;
                  },
                  {}
                );

                if (!attributeMappingObject["custom:role"]) {
                  actions.setFieldError(
                    "attributeMappings",
                    "Mapping for custom:role is required (choose its SAML group/role claim)."
                  );
                  return;
                }

                // Proceed with API or other logic
                let payload = {
                  providerName: values?.providerName,
                  metadata: {
                    attributeMapping: attributeMappingObject,
                    xml: values?.xmlMetadata,
                  },
                  orgId: selectedOrgId,
                  idpIdentifier: values?.idpIdentifier,
                  roleGroupMapping: {
                    "Organization Administrator": values.orgAdminGroup.trim(),
                    "Navigator": values.navigatorGroup.trim(),
                    "Clinician": values.clinicianGroup.trim(),
                  },
                  enableIDPSignout: values?.enableIDPSignout || false,
                };


                setIsLoading(true);
                editIDP(payload, selectedOrgId)
                  .then((res) => {
                    if (res?.status === true) {
                      success(res?.message || "IDP Updated Successfully");
                      navigate("/app/idp");
                    }
                  })
                  .catch((res) => {
                    failed(res?.errors?.[0]?.msg?.error);
                  })
                  .finally(() => setIsLoading(false));
              }}
            >
              {(formik) => (
                <form
                  className="common-form border-fields"
                  onSubmit={formik.handleSubmit}
                >
                  {/* SAML Assertion */}
                  <Row>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Provider Name</Form.Label>
                        <input
                          type="text"
                          name="providerName"
                          value={formik.values.providerName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Provider Name"
                          disabled
                          className={`form-control ${formik.touched.providerName &&
                            formik.errors.providerName
                            ? "is-invalid"
                            : ""
                            }`}
                        />
                        {formik.touched.providerName &&
                          formik.errors.providerName && (
                            <div className="invalid-feedback">
                              {formik.errors.providerName}
                            </div>
                          )}
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label>Identifier Domain</Form.Label>
                        <input
                          type="text"
                          name="idpIdentifier"
                          value={formik.values.idpIdentifier}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled
                          placeholder="Identifier Name"
                          className={`form-control ${formik.touched.idpIdentifier &&
                            formik.errors.idpIdentifier
                            ? "is-invalid"
                            : ""
                            }`}
                        />
                        {formik.touched.idpIdentifier &&
                          formik.errors.idpIdentifier && (
                            <div className="invalid-feedback">
                              {formik.errors.idpIdentifier}
                            </div>
                          )}
                      </Form.Group>
                      {/* {isSuperAdmin && (
                        <Col>
                          <Form.Group
                            className="mb-3"
                            style={{ position: "relative" }}
                          >
                            <Form.Label>Select Organization</Form.Label>
                            <div style={{ position: "relative" }}>
                              <select
                                name="selectedOrgId"
                                value={selectedOrgId}
                                onChange={(e) => {
                                  handleOrgChange(e);
                                }}
                                className={`form-control ${
                                  formik.touched.selectedOrgId &&
                                  formik.errors.selectedOrgId
                                    ? "is-invalid"
                                    : ""
                                }`}
                                style={{
                                  appearance: "none",
                                  WebkitAppearance: "none",
                                  paddingRight: "2rem",
                                }}
                              >
                                <option value="">Select Organization</option>
                                {organizations.map((org) => (
                                  <option key={org.id} value={org.id}>
                                    {org.name}
                                  </option>
                                ))}
                              </select>
                              {*/}
                      {/* Custom Dropdown Arrow */}
                      {/* <div
                                style={{
                                  position: "absolute",
                                  top: "50%",
                                  right: "1rem",
                                  pointerEvents: "none",
                                  transform: "translateY(-50%)",
                                  fontSize: "1.2rem",
                                  color: "#555",
                                }}
                              >
                                ▼
                              </div>
                            </div>

                            {formik.touched.selectedOrgId &&
                              formik.errors.selectedOrgId && (
                                <div className="invalid-feedback">
                                  {formik.errors.selectedOrgId}
                                </div>
                              )}
                          </Form.Group>
                        </Col>
                      )}  */}
                    </Col>
                  </Row>

                  {/* SAML Metadata */}
                  <Row>
                    <Col>
                      <h4 style={{ marginBottom: "20px", marginTop: "30px" }}>SAML Metadata</h4>
                      <Col>
                        <div className="mb-3">
                          <input
                            type="file"
                            accept=".xml"
                            onChange={(e) =>
                              handleFileUpload(e, formik.setFieldValue)
                            }
                            className="form-control"
                          />
                        </div>
                      </Col>

                      <div
                        style={{
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          overflow: "hidden",
                        }}
                      >
                        <Editor
                          height="300px"
                          defaultLanguage="xml"
                          value={formik.values.xmlMetadata}
                          onMount={(editor) => (editorRef.current = editor)}
                          onChange={(value) => {
                            formik.setFieldValue("xmlMetadata", value);

                            // Clear previous markers when user starts editing
                            monaco.editor.setModelMarkers(
                              editorRef.current.getModel(),
                              "owner",
                              []
                            );
                          }}
                          options={{
                            minimap: { enabled: true },
                            wordWrap: "off",
                            formatOnPaste: true,
                            formatOnType: true,
                            fontSize: 14,
                            scrollBeyondLastLine: false,
                          }}
                        />
                        {formik.errors.xmlMetadata &&
                          formik.touched.xmlMetadata && (
                            <div className="text-danger mt-1">
                              {formik.errors.xmlMetadata}
                            </div>
                          )}
                      </div>
                    </Col>
                  </Row>

                  <hr style={{ borderWidth: "5px" }} />

                  {/* Attribute Mappings */}
                  <Row className="mt-3">
                    <Col>
                      <h4 style={{ marginBottom: "20px" }}>Attribute Mappings</h4>
                      <FieldArray name="attributeMappings">
                        {({ push, remove }) => (
                          <>
                            {formik.values.attributeMappings.map(
                              (mapping, index) => {
                                const attrError =
                                  formik.errors.attributeMappings?.[index];
                                const attrTouched =
                                  formik.touched.attributeMappings?.[index];

                                const isMandatory =
                                  mapping.cognitoAttribute === "email" ||
                                  mapping.cognitoAttribute ===
                                  "custom:saml_unique_id" || mapping.cognitoAttribute === "custom:role"

                                return (
                                  <Row
                                    key={index}
                                    className="align-items-center mb-2"
                                  >
                                    {/*<Col md={5}>
                                      <input
                                        type="text"
                                        name={`attributeMappings[${index}].cognitoAttribute`}
                                        value={mapping.cognitoAttribute}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="Cognito Attribute"
                                        className={`form-control ${
                                          attrTouched?.cognitoAttribute &&
                                          attrError?.cognitoAttribute
                                            ? "is-invalid"
                                            : ""
                                        }`}
                                      />
                                      {attrTouched?.cognitoAttribute &&
                                        attrError?.cognitoAttribute && (
                                          <div className="invalid-feedback">
                                            {attrError.cognitoAttribute}
                                          </div>
                                        )}
                                    </Col>*/}

                                    <Col md={5}>
                                      <div style={{ position: "relative" }}>
                                        <select
                                          name={`attributeMappings[${index}].cognitoAttribute`}
                                          value={mapping.cognitoAttribute}
                                          onChange={formik.handleChange}
                                          onBlur={formik.handleBlur}
                                          disabled={isMandatory}
                                          className={`form-control ${attrTouched?.cognitoAttribute &&
                                            attrError?.cognitoAttribute
                                            ? "is-invalid"
                                            : ""
                                            }`}
                                          style={{
                                            appearance: "none",
                                            WebkitAppearance: "none",
                                            paddingRight: "2rem",
                                          }}
                                        >
                                          <option value="">
                                            Select Attribute
                                          </option>
                                          {COGNITO_ATTRIBUTES.filter((attr) => {
                                            const alreadyUsed =
                                              formik.values.attributeMappings
                                                .filter((_, i) => i !== index)
                                                .map((m) => m.cognitoAttribute);
                                            return (
                                              !alreadyUsed.includes(
                                                attr.Name
                                              ) ||
                                              attr.Name ===
                                              mapping.cognitoAttribute
                                            );
                                          }).map((attr) => (
                                            <option
                                              key={attr.Name}
                                              value={attr.Name}
                                            >
                                              {attr.Name}
                                            </option>
                                          ))}
                                        </select>

                                        {/* Dropdown icon */}
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: "50%",
                                            right: "1rem",
                                            pointerEvents: "none",
                                            transform: "translateY(-50%)",
                                            fontSize: "0.9rem",
                                            color: "#555",
                                          }}
                                        >
                                          ▼
                                        </div>

                                        {attrTouched?.cognitoAttribute &&
                                          attrError?.cognitoAttribute && (
                                            <div className="invalid-feedback">
                                              {attrError.cognitoAttribute}
                                            </div>
                                          )}
                                      </div>
                                    </Col>

                                    <Col md={5}>
                                      <input
                                        type="text"
                                        name={`attributeMappings[${index}].samlAttribute`}
                                        value={mapping.samlAttribute}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder="SAML Attribute"
                                        className={`form-control ${attrTouched?.samlAttribute &&
                                          attrError?.samlAttribute
                                          ? "is-invalid"
                                          : ""
                                          }`}
                                      />
                                      {attrTouched?.samlAttribute &&
                                        attrError?.samlAttribute && (
                                          <div className="invalid-feedback">
                                            {attrError.samlAttribute}
                                          </div>
                                        )}
                                    </Col>

                                    <Col md={2}>
                                      {!isMandatory && (
                                        <DeleteIcon
                                          onClick={() => remove(index)}
                                          style={{
                                            cursor: "pointer",
                                            color: "#c00",
                                          }}
                                          titleAccess="Remove"
                                        />
                                      )}
                                    </Col>
                                  </Row>
                                );
                              }
                            )}
                            <Button
                              type="button"
                              title="Add Attribute"
                              className="mt-3"
                              onClick={() =>
                                push({
                                  cognitoAttribute: "",
                                  samlAttribute: "",
                                })
                              }
                            >
                              + Add Attribute
                            </Button>
                          </>
                        )}
                      </FieldArray>
                    </Col>
                  </Row>
                  <hr style={{ borderWidth: "5px" }} />

                  {/* IDP Group → Florizel Role mapping */}
                  <Row className="mt-4">
                    <Col>
                      <h4 style={{ marginBottom: "20px" }}>Role Mapping (IDP Group → Florizel Role)</h4>

                      <Col className="align-items-center mb-2">
                        <Col md={6}>
                          <Form.Label>Organization Admin Group</Form.Label>
                          <input
                            type="text"
                            name="orgAdminGroup"
                            value={formik.values.orgAdminGroup}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="e.g. Florizel Organization Administrator"
                            className={`form-control ${formik.touched.orgAdminGroup && formik.errors.orgAdminGroup ? "is-invalid" : ""
                              }`}
                          />
                          {formik.touched.orgAdminGroup && formik.errors.orgAdminGroup && (
                            <div className="invalid-feedback">{formik.errors.orgAdminGroup}</div>
                          )}
                        </Col>

                        <Col md={6} style={{ marginTop: "7px" }}>
                          <Form.Label>Navigator Group</Form.Label>
                          <input
                            type="text"
                            name="navigatorGroup"
                            value={formik.values.navigatorGroup}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="e.g. Florizel Navigators"
                            className={`form-control ${formik.touched.navigatorGroup && formik.errors.navigatorGroup ? "is-invalid" : ""
                              }`}
                          />
                          {formik.touched.navigatorGroup && formik.errors.navigatorGroup && (
                            <div className="invalid-feedback">{formik.errors.navigatorGroup}</div>
                          )}
                        </Col>
                      </Col>

                      <Row className="align-items-center mb-2">
                        <Col md={6}>
                          <Form.Label>Clinician Group</Form.Label>
                          <input
                            type="text"
                            name="clinicianGroup"
                            value={formik.values.clinicianGroup}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="e.g. Florizel Clinicians"
                            className={`form-control ${formik.touched.clinicianGroup && formik.errors.clinicianGroup ? "is-invalid" : ""
                              }`}
                          />
                          {formik.touched.clinicianGroup && formik.errors.clinicianGroup && (
                            <div className="invalid-feedback">{formik.errors.clinicianGroup}</div>
                          )}
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <hr style={{ borderWidth: "5px" }} />

                  {/* Settings */}
                  <Row className="mt-4">
                    <Col>
                      <h4 style={{ marginBottom: "20px" }}>Settings</h4>
                      <Form.Group className="mb-3">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Form.Check
                            type="checkbox"
                            id="enableIDPSignout"
                            checked={formik.values.enableIDPSignout}
                            onChange={(e) => {
                              formik.setFieldValue("enableIDPSignout", e.target.checked);
                            }}
                            label="Enable IDP Sign Out Workflow"
                          />
                          <Tooltip title="Enabling this will log you out from IDP as well when you logout from webapp">
                            <InfoIcon style={{ fontSize: "18px", color: "#6c757d", cursor: "help", marginBottom: "4px", marginLeft: "15px" }} />
                          </Tooltip>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Submit Buttons */}
                  <div
                    className="btn-wrap"
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "2rem",
                      gap: "1rem",
                    }}
                  >
                    <Button
                      variant="secondary"
                      title="Cancel"
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">Update</Button>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        ) : (
          <FullPageSpinner loadingText="Loading..." />
        )}
      </section>
    </>
  );
};

export default IDPUpdate;
